import { z } from 'zod';
import type { BaseEntity } from './common';
import type { WorkerIntegration } from './workerIntegration';
import type { WorkingRelationship } from './workingRelationship';
import type { WorkerHolidayPerYear } from './workerHolidayPerYear';
import type { JobOrderManager } from './jobOrderManager';
import type { JobOrderAllowedWorker } from './jobOrderAllowedWorker';

/**
 * Gender enum - Enumerazione genere del lavoratore
 * Corrisponde all'enum backend Gender
 */
export enum Gender {
	Unknown = 0,
	Male = 10,
	Female = 20,
}

/**
 * HolidayStatus - Status delle ferie per anno
 * Corrisponde al modello backend per getHolidayStatus
 */
export interface HolidayStatus {
	/** ID del lavoratore */
	workerId: number;
	/** Anno di riferimento */
	year: number;
	/** Ore residuo anni precedenti */
	remainingBalanceHours: number;
	/** Ore ferie annuali spettanti */
	yearlyHolidayHours: number;
	/** Ore pianificate */
	planned: number;
	/** Ore da trasferire anno prossimo */
	nextYearHours: number;
	/** Ore consumate alla data */
	consumedAtDate: number;
	/** Ore totali disponibili */
	totalHours: number;
	/** Ore da pianificare */
	toPlanHours: number;
}

/**
 * Worker - Entità lavoratore
 * Corrisponde al modello backend Worker con tutte le proprietà
 */
export interface Worker extends BaseEntity {
	/** Codice identificativo (required, max 100 chars) */
	code: string;
	/** Nome completo */
	name?: string;
	/** Codice fiscale */
	socialSecurityNumber?: string;
	/** Genere (enum numerico) */
	gender?: Gender | null;
	/** Data di nascita */
	birthDate?: string | null; // ISO date string dal backend
	/** URL avatar */
	avatarUrl?: string | null;
	/** Immagine avatar (base64) */
	avatarImage?: string | null;
	/** ID utente associato */
	userId?: string;
	/** Note */
	notes?: string | null;
	/** Può essere allocato su progetti */
	isAllocable?: boolean | null;
	/** Escluso dall'export */
	isExcludedFromExport?: boolean | null;

	// Navigation properties
	/** Ordini gestiti dal lavoratore */
	managedOrders?: JobOrderManager[];
	/** Ordini su cui può lavorare */
	orders?: JobOrderAllowedWorker[];
	/** Rapporti di lavoro */
	workingRelationships?: WorkingRelationship[];
	/** Ferie per anno */
	workerHolidaysPerYear?: WorkerHolidayPerYear[];
	/** Appartenenza a gruppi (TODO: importare GroupWorker quando disponibile) */
	memberOf?: any[];
	/** Utente associato (navigation property) */
	user?: {
		id: string;
		userName?: string;
		enabled: boolean;
		email?: string;
		roles?: string[];
	};
	/** Integrazioni con sistemi esterni */
	integrations?: WorkerIntegration[];
	/** Competenze del lavoratore (TODO: implementare quando necessario) */
	// skills?: any[]; // TODO: Implementare quando necessario
}

/**
 * Tipo per il risultato dell'autocomplete Workers
 * Corrisponde ai campi selezionati nella query OData: id, code, name + expand user (userName, enabled)
 */
export type WorkerAutocompleteResult = Pick<Worker, 'id' | 'code' | 'name'> & {
	/** Utente associato (expandato) */
	user?: {
		/** Nome utente */
		userName: string;
		/** Utente abilitato */
		enabled: boolean;
	};
};

/**
 * Schema Zod per la validazione runtime dei Gender
 */
export const GenderSchema = z.nativeEnum(Gender);

/**

/**
 * Schema Zod per la validazione runtime dei HolidayStatus
 */
export const HolidayStatusSchema = z.object({
	workerId: z.number(),
	year: z.number(),
	remainingBalanceHours: z.number(),
	yearlyHolidayHours: z.number(),
	planned: z.number(),
	nextYearHours: z.number(),
	consumedAtDate: z.number(),
	totalHours: z.number(),
	toPlanHours: z.number(),
});

/**
 * Schema Zod per la validazione runtime dei Worker
 */
export const WorkerSchema = z.object({
	id: z.number(),
	code: z.string().min(1).max(100),
	name: z.string().optional(),
	socialSecurityNumber: z.string().optional(),
	gender: GenderSchema.nullish(),
	birthDate: z.string().nullish(), // Date arriva come string ISO dal backend
	avatarUrl: z.string().nullish(), // Può essere string, null o undefined
	avatarImage: z.string().nullish(), // Può essere string, null o undefined
	userId: z.string().optional(),
	notes: z.string().nullish(), // Può essere string, null o undefined
	isAllocable: z.boolean().nullish(),
	isExcludedFromExport: z.boolean().nullish(),
	createdAt: z.string().optional(), // Potrebbe non essere sempre presente
	modifiedAt: z.string().optional(),
	createdBy: z.string().optional(),
	modifiedBy: z.string().optional(),
	// Navigation properties sono opzionali
	managedOrders: z.array(z.any()).optional(),
	orders: z.array(z.any()).optional(),
	workingRelationships: z.array(z.any()).optional(),
	workerHolidaysPerYear: z.array(z.any()).optional(),
	memberOf: z.array(z.any()).optional(),
	// user: z.any().optional(), // TODO: Implementare quando necessario
	integrations: z.array(z.any()).optional(),
	// skills: z.array(z.any()).optional(), // TODO: Implementare quando necessario
});
